import {Injectable} from "@angular/core";
import {Facility} from "../../../../server/entities/facility.entity";
import {Gathering} from "../../../../server/entities/gathering.entity";
import {News} from "../../../../server/entities/news.entity";
import {User} from "../../../../server/entities/user.entity";
import {FacilityGetRequest} from "../../../../server/services/facility/get.contract";
import {UserUpdateRequest} from "../../../../server/services/user/update.contract";
import {FacilityQueryRequest} from "../../../../server/services/facility/query.contract";
import {GatheringQueryRequest} from "../../../../server/services/gathering/query.contract";
import {NewsQueryRequest} from "../../../../server/services/news/query.contract";
import {UserQueryRequest} from "../../../../server/services/user/query.contract";

@Injectable({
  providedIn: 'root'
})
export class SharedDataService {
  public facilities: Map<string, Facility> = new Map<string, Facility>();
  public gatherings: Map<string, Gathering> = new Map<string, Gathering>();
  public news: Map<string, News> = new Map<string, News>();
  public users: Map<string, User> = new Map<string, User>();

  private isInitialized = false;
  private currentUser?: User;
  private currentIsAdmin?: boolean;
  private currentIsCreator?: boolean;

  constructor() {
    // Set up page visibility listener for cache refresh
    this.setupPageVisibilityListener();
    // Note: Cache initialization is now handled by APP_INITIALIZER or manual calls
  }

  /**
   * Set up listener for page visibility changes to refresh cache when user returns to tab
   */
  private setupPageVisibilityListener() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', async () => {
        if (!document.hidden && this.isInitialized) {
          // User returned to the tab, optionally refresh cache
          // You can make this more intelligent by tracking last refresh time
          const lastRefresh = localStorage.getItem('sharedDataService_lastRefresh');
          const now = Date.now();
          const fiveMinutes = 5 * 60 * 1000; // 5 minutes

          if (!lastRefresh || (now - parseInt(lastRefresh)) > fiveMinutes) {
            console.log('SharedDataService: Refreshing cache due to page visibility change');

            // Use stored user context for proper refresh
            this.reloadCache(this.currentUser, this.currentIsAdmin, this.currentIsCreator);

            localStorage.setItem('sharedDataService_lastRefresh', now.toString());
          }
        }
      });
    }
  }

  /**
   * Initialize the cache with user-context-aware data.
   * This should be called with proper user context after authentication.
   */
  async initializeCache(user?: User, isAdmin?: boolean, isCreator?: boolean) {
    if (this.isInitialized) {
      return;
    }

    try {
      console.log('SharedDataService: Initializing cache...');

      // Store current user context for future cache refreshes
      this.currentUser = user;
      this.currentIsAdmin = isAdmin;
      this.currentIsCreator = isCreator;

      if (isAdmin) {
        console.log('SharedDataService: Loading admin data...');
        await this.loadAllData();
      } else if (isCreator) {
        console.log('SharedDataService: Loading creator-specific data...');
        await this.loadCreatorData(user);
      } else {
        console.log('SharedDataService: Loading basic data...');
        await this.loadBasicData();
      }

      this.isInitialized = true;
      console.log('SharedDataService: Cache initialization complete');

    } catch (error) {
      console.warn('SharedDataService: Failed to initialize cache:', error);
      // Don't throw error to prevent app startup issues
    }
  }

  /**
   * Load all data for admin users
   * Note: Facilities are not loaded here as the admin facilities page handles its own pagination
   */
  private async loadAllData() {
    const [gatherings, news] = await Promise.all([
      new GatheringQueryRequest({}).execute(),
      new NewsQueryRequest({}).execute()
    ]);

    // Don't load all facilities for admin users to prevent race conditions with paginated views
    // Individual pages that need facilities will load them separately
    this.storeGatherings(gatherings);
    this.storeNews(news);

    console.log(`SharedDataService: Loaded ${gatherings.length} gatherings, ${news.length} news items (admin) - facilities loaded separately by pages`);
  }

  /**
   * Load filtered data for creator users
   */
  private async loadCreatorData(user: User) {
    // If creator has no owned facilities, load empty data
    if (!user?.ownedFacilities?.length) {
      console.log('SharedDataService: Creator has no owned facilities, loading empty data');
      // Store empty arrays - creator should see nothing until they own facilities
      this.storeFacilities([]);
      this.storeGatherings([]);
      this.storeNews([]);
      this.storeUsers([]);
      console.log('SharedDataService: Loaded 0 facilities, 0 gatherings, 0 news items, 0 subscribers (creator with no facilities)');
      return;
    }

    // Load creator's owned facilities
    const ownedFacilities = await new FacilityQueryRequest({
      idList: user.ownedFacilities
    }).execute();
    this.storeFacilities(ownedFacilities);

    // Load news filtered by owned facilities
    const creatorNews = await new NewsQueryRequest({
      filter: { owningFacility: { $in: user.ownedFacilities } },
      includeAnalytics: true
    }).execute();
    this.storeNews(creatorNews);

    // Load gatherings filtered by owned facilities
    const creatorGatherings = await new GatheringQueryRequest({
      filter: { owningFacility: { $in: user.ownedFacilities } }
    }).execute();
    this.storeGatherings(creatorGatherings);

    // Load subscribers (users who liked the creator's facilities) but exclude the creator themselves
    try {
      const allSubscribers: User[] = await new UserQueryRequest({
        likedFacilities: { $in: user.ownedFacilities }
      }).execute();

      // Filter out the current user from the subscriber list
      const filteredSubscribers = allSubscribers.filter(subscriber => subscriber.id !== user.id);
      this.storeUsers(filteredSubscribers);

      console.log(`SharedDataService: Loaded ${ownedFacilities.length} facilities, ${creatorGatherings.length} gatherings, ${creatorNews.length} news items, ${filteredSubscribers.length} subscribers (creator)`);
    } catch (error) {
      console.warn('SharedDataService: Failed to load subscribers for creator:', error);
      console.log(`SharedDataService: Loaded ${ownedFacilities.length} facilities, ${creatorGatherings.length} gatherings, ${creatorNews.length} news items (creator)`);
    }
  }

  /**
   * Load basic data for regular users or fallback
   */
  private async loadBasicData() {
    // Load a limited set of data for performance
    const [facilities, gatherings] = await Promise.all([
      new FacilityQueryRequest({}).execute(),
      new GatheringQueryRequest({}).execute()
    ]);

    this.storeFacilities(facilities);
    this.storeGatherings(gatherings);

    console.log(`SharedDataService: Loaded ${facilities.length} facilities, ${gatherings.length} gatherings (basic)`);
  }

  /**
   * Force reload all cached data. Can be called manually when needed.
   */
  async reloadCache(user?: User, isAdmin?: boolean, isCreator?: boolean) {
    this.clear();
    this.isInitialized = false;
    await this.initializeCache(user, isAdmin, isCreator);
  }

  /**
   * Initialize cache for a specific user context after login
   * This method should be called after successful login to ensure proper cache setup
   */
  async initializeCacheForUser(user: User, isAdmin: boolean, isCreator: boolean) {
    // Clear existing cache first
    this.clear();
    this.isInitialized = false;

    // Initialize with user context
    await this.initializeCache(user, isAdmin, isCreator);
  }

  /**
   * Ensure cache is properly initialized for current user context
   * This can be called from pages that need to ensure cache is ready
   */
  async ensureCacheInitialized(user?: User, isAdmin?: boolean, isCreator?: boolean) {
    if (!this.isInitialized && user) {
      await this.initializeCache(user, isAdmin, isCreator);
    }
  }

  clear() {
    this.facilities.clear();
    this.gatherings.clear();
    this.news.clear();
    this.users.clear();
    this.isInitialized = false;
    // Reset user context tracking
    this.currentUser = undefined;
    this.currentIsAdmin = undefined;
    this.currentIsCreator = undefined;
  }

  async storeFacilityById(facilityId: string) {
    const facility: Facility = await new FacilityGetRequest({id: facilityId}).execute();
    this.facilities.set(facility.id, facility);
  }

  storeFacilities(facilities: Facility[]) {
    for (const facility of facilities) {
      this.facilities.set(facility.id, facility);
    }
  }

  // Returns the user's owned facilities that still need the facility-onboarding
  // wizard (see Facility.needsOnboarding). Uses the cache when populated,
  // otherwise queries the owned facilities directly.
  async getPendingOnboardingFacilities(user: User): Promise<Facility[]> {
    if (!user?.ownedFacilities || user.ownedFacilities.length === 0) {
      return [];
    }
    let owned: Facility[] = user.ownedFacilities
      .map(id => this.facilities.get(id))
      .filter(f => !!f);
    if (owned.length < user.ownedFacilities.length) {
      owned = await new FacilityQueryRequest({ idList: user.ownedFacilities }).execute();
      this.storeFacilities(owned);
    }
    return owned.filter(f => Facility.needsOnboarding(f));
  }

  storeGatherings(gatherings: Gathering[]) {
    for (const gathering of gatherings) {
      this.gatherings.set(gathering.id, gathering);
    }
  }

  storeNews(news: News[]) {
    for (const newsItem of news) {
      this.news.set(newsItem.id, newsItem);
    }
  }

  storeUsers(users: User[]) {
    for (const user of users) {
      this.users.set(user.id, user);
    }
  }

  storeUser(user: User) {
    this.users.set(user.id, user);
  }

  /**
   * Updates a user in both the database and cache, and returns the updated user.
   * This method ensures that both SharedDataService cache and WebAuthenticationService cache are kept in sync.
   */
  async updateUser(user: User): Promise<User> {
    // Update in database
    await new UserUpdateRequest(user).execute();

    // Update in local cache
    this.users.set(user.id, user);

    return user;
  }

  /**
   * Helper method to add a facility to a user's ownedFacilities and update all caches.
   * This ensures consistent cache management across the application.
   */
  async addFacilityToUserOwned(user: User, facilityId: string): Promise<User> {
    if (!user.ownedFacilities) {
      user.ownedFacilities = [];
    }

    if (!user.ownedFacilities.includes(facilityId)) {
      user.ownedFacilities.push(facilityId);

      // Update user in database and cache
      await this.updateUser(user);

      // Also update the facility cache
      await this.storeFacilityById(facilityId);
    }

    return user;
  }
}
